import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { makeApi, type Api, type Me, type Row } from "./api";
import type { Names } from "./format";

// Until they load, or if loading fails, the four seeded case types stand in; the server validates every key.
const SEED_CASE_TYPES: [string, string][] = [
  ["goal_change", "Goal change"], ["other", "Other"], ["reflux_gi", "Reflux / GI"],
  ["restart_after_stop", "Restart after stop"],
];

export type Admin = Names & {
  key: string;
  api: Api;
  me: Me | undefined;
  caseTypes: [string, string][];
  options: Row | undefined;
  /** Opens a customer on the Customers tab (queue and staff-change rows link here). */
  openCustomer: (id: string | null) => void;
  /** Opens the queue (the waiting chip links here). */
  openQueue: () => void;
};

const Ctx = createContext<Admin | null>(null);

export function useAdmin(): Admin {
  const a = useContext(Ctx);
  if (!a) throw new Error("useAdmin outside AdminProvider");
  return a;
}

export const meQuery = (key: string, api: Api) => ({ queryKey: [key, "me"], queryFn: () => api.get<Me>("me") });

export function AdminProvider({ moduleKey, base, children }: { moduleKey: string; base: string; children: ReactNode }) {
  const api = useMemo(() => makeApi(base), [base]);
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const me = useQuery(meQuery(moduleKey, api));
  const ct = useQuery({ queryKey: [moduleKey, "case-types"], queryFn: () => api.get<Row[]>("case-types") });
  const opts = useQuery({ queryKey: [moduleKey, "program-options"], queryFn: () => api.get<Row>("program-options") });

  const value = useMemo<Admin>(() => {
    const caseTypes: [string, string][] =
      Array.isArray(ct.data) && ct.data.length ? ct.data.map((t) => [t.key, t.label]) : SEED_CASE_TYPES;
    const names: Record<string, string> = {};
    for (const x of (opts.data?.products || []) as Row[])
      names[x.product_key] = x.name + (x.flavour ? ` (${x.flavour})` : "");
    return {
      key: moduleKey,
      api,
      me: me.data,
      caseTypes,
      options: opts.data,
      caseType: (k) => (caseTypes.find((c) => c[0] === k) || [k, k])[1],
      product: (k) => (k && names[k]) || k || "—",
      openCustomer: (id) =>
        void navigate({
          to: "/app/$area/$module/$tab",
          params: { area: params.area ?? "experience_delivery", module: params.module ?? moduleKey, tab: "customers" },
          search: id ? { customer: id } : {},
        }),
      openQueue: () =>
        void navigate({
          to: "/app/$area/$module/$tab",
          params: { area: params.area ?? "experience_delivery", module: params.module ?? moduleKey, tab: "queue" },
          search: {},
        }),
    };
  }, [api, ct.data, opts.data, me.data, moduleKey, navigate, params.area, params.module]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
